import { Lesson } from './types';
import { vocabLesson } from './vocab';

/**
 * Further vocabulary for the Basic Korean, EPS Vocabulary and Daily Life units,
 * added after each unit's own lessons. Everyday and workplace words, written
 * for Prepyo; nothing here duplicates a word those units already teach.
 */

export const MORE_BASIC: Lesson[] = [
  vocabLesson('native-numbers', 'Native numbers', 'One to ten for counting, age and hours', [
    { ko: '하나', roman: 'ha-na', en: 'one (counting)', note: 'Native numbers count things, people, age and hours. 하나, 둘, 셋, 넷 become 한, 두, 세, 네 before a counter: 한 개, 두 시.' },
    { ko: '둘', roman: 'dul', en: 'two (counting)' },
    { ko: '셋', roman: 'set', en: 'three (counting)' },
    { ko: '넷', roman: 'net', en: 'four (counting)' },
    { ko: '다섯', roman: 'da-seot', en: 'five (counting)' },
    { ko: '여섯', roman: 'yeo-seot', en: 'six (counting)' },
    { ko: '일곱', roman: 'il-gop', en: 'seven (counting)' },
    { ko: '여덟', roman: 'yeo-deol', en: 'eight (counting)' },
    { ko: '아홉', roman: 'a-hop', en: 'nine (counting)' },
    { ko: '열', roman: 'yeol', en: 'ten (counting)', note: '열 also means “fever”.' },
  ]),
  vocabLesson('family', 'Family', 'Father, mother, wife, husband and children', [
    { ko: '아버지', roman: 'a-beo-ji', en: 'father', picture: '👨' },
    { ko: '어머니', roman: 'eo-meo-ni', en: 'mother', picture: '👩' },
    { ko: '아내', roman: 'a-nae', en: 'wife' },
    { ko: '남편', roman: 'nam-pyeon', en: 'husband' },
    { ko: '아들', roman: 'a-deul', en: 'son', picture: '👦' },
    { ko: '딸', roman: 'ttal', en: 'daughter', picture: '👧' },
    { ko: '형', roman: 'hyeong', en: "older brother (a man's)", note: 'A woman calls her older brother 오빠 (o-ppa).' },
    { ko: '누나', roman: 'nu-na', en: "older sister (a man's)", note: 'A woman calls her older sister 언니 (eon-ni).' },
    { ko: '동생', roman: 'dong-saeng', en: 'younger brother or sister' },
    { ko: '할머니', roman: 'hal-meo-ni', en: 'grandmother', picture: '👵' },
  ]),
  vocabLesson('body', 'Body', 'Head, eyes, hands, feet and back', [
    { ko: '머리', roman: 'meo-ri', en: 'head' },
    { ko: '눈', roman: 'nun', en: 'eye', picture: '👁️', note: '눈 also means “snow”.' },
    { ko: '코', roman: 'ko', en: 'nose', picture: '👃' },
    { ko: '입', roman: 'ip', en: 'mouth', picture: '👄' },
    { ko: '귀', roman: 'gwi', en: 'ear', picture: '👂' },
    { ko: '손', roman: 'son', en: 'hand', picture: '✋' },
    { ko: '발', roman: 'bal', en: 'foot', picture: '🦶' },
    { ko: '다리', roman: 'da-ri', en: 'leg', picture: '🦵' },
    { ko: '팔', roman: 'pal', en: 'arm', picture: '💪' },
    { ko: '허리', roman: 'heo-ri', en: 'lower back, waist' },
  ]),
  vocabLesson('time-words', 'Time words', 'Today, tomorrow, morning, night and every day', [
    { ko: '오늘', roman: 'o-neul', en: 'today' },
    { ko: '내일', roman: 'nae-il', en: 'tomorrow' },
    { ko: '어제', roman: 'eo-je', en: 'yesterday' },
    { ko: '아침', roman: 'a-chim', en: 'morning', picture: '🌅' },
    { ko: '점심', roman: 'jeom-sim', en: 'lunch, midday', picture: '🍱' },
    { ko: '저녁', roman: 'jeo-nyeok', en: 'evening', picture: '🌆' },
    { ko: '밤', roman: 'bam', en: 'night', picture: '🌙' },
    { ko: '주말', roman: 'ju-mal', en: 'weekend' },
    { ko: '매일', roman: 'mae-il', en: 'every day' },
    { ko: '나중에', roman: 'na-jung-e', en: 'later' },
  ]),
  vocabLesson('days-months', 'Days & months', 'Monday to Sunday, and naming the months', [
    { ko: '월요일', roman: 'wo-ryo-il', en: 'Monday' },
    { ko: '화요일', roman: 'hwa-yo-il', en: 'Tuesday' },
    { ko: '수요일', roman: 'su-yo-il', en: 'Wednesday' },
    { ko: '목요일', roman: 'mo-gyo-il', en: 'Thursday' },
    { ko: '금요일', roman: 'geu-myo-il', en: 'Friday' },
    { ko: '토요일', roman: 'to-yo-il', en: 'Saturday' },
    { ko: '일요일', roman: 'i-ryo-il', en: 'Sunday' },
    { ko: '일월', roman: 'i-rwol', en: 'January', note: 'Months are a number + 월: 일월 (1), 이월 (2), 삼월 (3) …' },
    { ko: '유월', roman: 'yu-wol', en: 'June', note: 'June is 유월, not 육월.' },
    { ko: '시월', roman: 'si-wol', en: 'October', note: 'October is 시월, not 십월.' },
  ]),
  vocabLesson('colours', 'Colours', 'Red, blue, yellow, green and more', [
    { ko: '빨간색', roman: 'ppal-gan-saek', en: 'red', picture: '🟥' },
    { ko: '파란색', roman: 'pa-ran-saek', en: 'blue', picture: '🟦' },
    { ko: '노란색', roman: 'no-ran-saek', en: 'yellow', picture: '🟨' },
    { ko: '초록색', roman: 'cho-rok-saek', en: 'green', picture: '🟩' },
    { ko: '검은색', roman: 'geo-meun-saek', en: 'black', picture: '⬛' },
    { ko: '흰색', roman: 'hin-saek', en: 'white', picture: '⬜' },
    { ko: '회색', roman: 'hoe-saek', en: 'grey' },
    { ko: '주황색', roman: 'ju-hwang-saek', en: 'orange', picture: '🟧' },
    { ko: '보라색', roman: 'bo-ra-saek', en: 'purple', picture: '🟪' },
    { ko: '갈색', roman: 'gal-saek', en: 'brown', picture: '🟫' },
  ]),
  vocabLesson('more-verbs', 'More verbs', 'Get up, wash, wait, help and know', [
    { ko: '일어나요', roman: 'i-reo-na-yo', en: 'get up' },
    { ko: '앉아요', roman: 'an-ja-yo', en: 'sit' },
    { ko: '씻어요', roman: 'ssi-seo-yo', en: 'wash' },
    { ko: '기다려요', roman: 'gi-da-ryeo-yo', en: 'wait' },
    { ko: '도와줘요', roman: 'do-wa-jwo-yo', en: 'help' },
    { ko: '알아요', roman: 'a-ra-yo', en: 'know' },
    { ko: '몰라요', roman: 'mol-la-yo', en: "don't know" },
    { ko: '배워요', roman: 'bae-wo-yo', en: 'learn' },
    { ko: '사요', roman: 'sa-yo', en: 'buy' },
    { ko: '줘요', roman: 'jwo-yo', en: 'give' },
  ]),
  vocabLesson('describing', 'Describing words', 'Big, small, heavy, hot and fast', [
    { ko: '커요', roman: 'keo-yo', en: 'big' },
    { ko: '작아요', roman: 'ja-ga-yo', en: 'small' },
    { ko: '많아요', roman: 'ma-na-yo', en: 'a lot, many' },
    { ko: '적어요', roman: 'jeo-geo-yo', en: 'a little, few' },
    { ko: '무거워요', roman: 'mu-geo-wo-yo', en: 'heavy', picture: '🏋️' },
    { ko: '가벼워요', roman: 'ga-byeo-wo-yo', en: 'light (not heavy)', picture: '🪶' },
    { ko: '뜨거워요', roman: 'tteu-geo-wo-yo', en: 'hot (to touch)', picture: '🔥' },
    { ko: '차가워요', roman: 'cha-ga-wo-yo', en: 'cold (to touch)', picture: '🧊' },
    { ko: '빨라요', roman: 'ppal-la-yo', en: 'fast', picture: '🐇' },
    { ko: '느려요', roman: 'neu-ryeo-yo', en: 'slow', picture: '🐢' },
  ]),
];

export const MORE_EPS_VOCAB: Lesson[] = [
  vocabLesson('more-tools', 'More tools', 'Screwdriver, wrench, pliers, torch and forklift', [
    { ko: '드라이버', roman: 'deu-ra-i-beo', en: 'screwdriver', picture: '🪛' },
    { ko: '렌치', roman: 'ren-chi', en: 'wrench', picture: '🔧' },
    { ko: '펜치', roman: 'pen-chi', en: 'pliers' },
    { ko: '테이프', roman: 'te-i-peu', en: 'tape' },
    { ko: '끈', roman: 'kkeun', en: 'string, cord', picture: '🧶' },
    { ko: '접착제', roman: 'jeop-chak-je', en: 'glue' },
    { ko: '손전등', roman: 'son-jeon-deung', en: 'torch', picture: '🔦' },
    { ko: '카트', roman: 'ka-teu', en: 'trolley', picture: '🛒' },
    { ko: '지게차', roman: 'ji-ge-cha', en: 'forklift' },
    { ko: '공구함', roman: 'gong-gu-ham', en: 'toolbox', picture: '🧰' },
  ]),
  vocabLesson('workplace-people', 'People at work', 'Team leader, manager, employee and interpreter', [
    { ko: '반장님', roman: 'ban-jang-nim', en: 'team leader' },
    { ko: '과장님', roman: 'gwa-jang-nim', en: 'manager' },
    { ko: '직원', roman: 'ji-gwon', en: 'employee' },
    { ko: '근로자', roman: 'geul-lo-ja', en: 'worker' },
    { ko: '외국인', roman: 'oe-gu-gin', en: 'foreigner' },
    { ko: '통역', roman: 'tong-yeok', en: 'interpreting, interpreter' },
    { ko: '관리자', roman: 'gwal-li-ja', en: 'supervisor' },
    { ko: '신입', roman: 'si-nip', en: 'new employee' },
    { ko: '경비원', roman: 'gyeong-bi-won', en: 'security guard' },
    { ko: '운전기사', roman: 'un-jeon-gi-sa', en: 'driver' },
  ]),
  vocabLesson('contract-pay', 'Contract & pay', 'Contract, wages, hourly pay, tax and insurance', [
    { ko: '계약서', roman: 'gye-yak-seo', en: 'contract', picture: '📄' },
    { ko: '급여', roman: 'geu-byeo', en: 'wages' },
    { ko: '시급', roman: 'si-geup', en: 'hourly pay' },
    { ko: '수당', roman: 'su-dang', en: 'allowance, extra pay' },
    { ko: '보너스', roman: 'bo-neo-seu', en: 'bonus' },
    { ko: '세금', roman: 'se-geum', en: 'tax' },
    { ko: '보험', roman: 'bo-heom', en: 'insurance' },
    { ko: '퇴직금', roman: 'toe-jik-geum', en: 'severance pay' },
    { ko: '월급날', roman: 'wol-geum-nal', en: 'payday' },
    { ko: '사인', roman: 'sa-in', en: 'signature', picture: '✍️' },
  ]),
  vocabLesson('hours-leave', 'Hours & leave', 'Start and finish work, overtime and days off', [
    { ko: '출근', roman: 'chul-geun', en: 'going to work' },
    { ko: '퇴근', roman: 'toe-geun', en: 'leaving work' },
    { ko: '야근', roman: 'ya-geun', en: 'night work' },
    { ko: '잔업', roman: 'ja-neop', en: 'overtime' },
    { ko: '근무 시간', roman: 'geun-mu si-gan', en: 'working hours' },
    { ko: '휴일', roman: 'hyu-il', en: 'day off' },
    { ko: '연차', roman: 'yeon-cha', en: 'annual leave' },
    { ko: '병가', roman: 'byeong-ga', en: 'sick leave' },
    { ko: '지각', roman: 'ji-gak', en: 'being late' },
    { ko: '결근', roman: 'gyeol-geun', en: 'absence from work' },
  ]),
  vocabLesson('factory-work', 'Factory work', 'Production, assembly, inspection and defects', [
    { ko: '생산', roman: 'saeng-san', en: 'production' },
    { ko: '조립', roman: 'jo-rip', en: 'assembly' },
    { ko: '검사', roman: 'geom-sa', en: 'inspection', picture: '🔍' },
    { ko: '포장', roman: 'po-jang', en: 'packing', picture: '📦' },
    { ko: '운반', roman: 'un-ban', en: 'carrying, transport' },
    { ko: '불량', roman: 'bul-lyang', en: 'defect' },
    { ko: '제품', roman: 'je-pum', en: 'product' },
    { ko: '부품', roman: 'bu-pum', en: 'part, component', picture: '⚙️' },
    { ko: '라인', roman: 'ra-in', en: 'production line' },
    { ko: '재고', roman: 'jae-go', en: 'stock' },
  ]),
  vocabLesson('materials', 'Materials', 'Iron, plastic, paper, wood and oil', [
    { ko: '철', roman: 'cheol', en: 'iron' },
    { ko: '플라스틱', roman: 'peul-la-seu-tik', en: 'plastic' },
    { ko: '종이', roman: 'jong-i', en: 'paper', picture: '📄' },
    { ko: '나무', roman: 'na-mu', en: 'wood', picture: '🪵' },
    { ko: '유리', roman: 'yu-ri', en: 'glass', picture: '🪟' },
    { ko: '고무', roman: 'go-mu', en: 'rubber' },
    { ko: '옷감', roman: 'ot-gam', en: 'fabric', picture: '🧵' },
    { ko: '기름', roman: 'gi-reum', en: 'oil', picture: '🛢️' },
    { ko: '페인트', roman: 'pe-in-teu', en: 'paint', picture: '🎨' },
    { ko: '전선', roman: 'jeon-seon', en: 'electric wire' },
  ]),
  vocabLesson('work-actions', 'Work actions', 'Move, lift, cut, wipe, check and fix', [
    { ko: '옮겨요', roman: 'om-gyeo-yo', en: 'move (something)' },
    { ko: '들어요', roman: 'deu-reo-yo', en: 'lift' },
    { ko: '놓아요', roman: 'no-a-yo', en: 'put down' },
    { ko: '잘라요', roman: 'jal-la-yo', en: 'cut', picture: '✂️' },
    { ko: '닦아요', roman: 'da-kka-yo', en: 'wipe' },
    { ko: '정리해요', roman: 'jeong-ni-hae-yo', en: 'tidy up' },
    { ko: '확인해요', roman: 'hwa-gin-hae-yo', en: 'check' },
    { ko: '고쳐요', roman: 'go-chyeo-yo', en: 'fix', picture: '🔧' },
    { ko: '눌러요', roman: 'nul-leo-yo', en: 'press' },
    { ko: '바꿔요', roman: 'ba-kkwo-yo', en: 'change, swap' },
  ]),
  vocabLesson('emergencies', 'Emergencies', 'Fire, ambulance, police, help and first aid', [
    { ko: '불이야', roman: 'bu-ri-ya', en: 'Fire!', picture: '🔥' },
    { ko: '화재', roman: 'hwa-jae', en: 'a fire (emergency)' },
    { ko: '비상구', roman: 'bi-sang-gu', en: 'emergency exit', picture: '🚪' },
    { ko: '구급차', roman: 'gu-geup-cha', en: 'ambulance', picture: '🚑' },
    { ko: '경찰', roman: 'gyeong-chal', en: 'police', picture: '👮' },
    { ko: '도와주세요', roman: 'do-wa-ju-se-yo', en: 'Please help!' },
    { ko: '다쳤어요', roman: 'da-chyeo-sseo-yo', en: "I'm hurt", picture: '🤕' },
    { ko: '피', roman: 'pi', en: 'blood', picture: '🩸' },
    { ko: '대피하세요', roman: 'dae-pi-ha-se-yo', en: 'Evacuate!' },
    { ko: '응급실', roman: 'eung-geup-sil', en: 'emergency room', picture: '🏥' },
  ]),
  vocabLesson('positions', 'Where things are', 'On, under, in front, behind, left and right', [
    { ko: '위', roman: 'wi', en: 'on, above', picture: '⬆️' },
    { ko: '아래', roman: 'a-rae', en: 'under, below', picture: '⬇️' },
    { ko: '앞', roman: 'ap', en: 'in front' },
    { ko: '뒤', roman: 'dwi', en: 'behind' },
    { ko: '옆', roman: 'yeop', en: 'next to' },
    { ko: '안', roman: 'an', en: 'inside', note: '안 before a verb means “not”: 안 가요.' },
    { ko: '밖', roman: 'bak', en: 'outside' },
    { ko: '오른쪽', roman: 'o-reun-jjok', en: 'right (side)', picture: '➡️' },
    { ko: '왼쪽', roman: 'oen-jjok', en: 'left (side)', picture: '⬅️' },
    { ko: '가운데', roman: 'ga-un-de', en: 'middle' },
  ]),
];

export const MORE_DAILY_LIFE: Lesson[] = [
  vocabLesson('food-drink', 'Food & drink', 'Soup, meat, bread, coffee and spicy', [
    { ko: '국', roman: 'guk', en: 'soup', picture: '🍲' },
    { ko: '고기', roman: 'go-gi', en: 'meat', picture: '🥩' },
    { ko: '빵', roman: 'ppang', en: 'bread', picture: '🍞' },
    { ko: '과일', roman: 'gwa-il', en: 'fruit', picture: '🍎' },
    { ko: '반찬', roman: 'ban-chan', en: 'side dishes' },
    { ko: '커피', roman: 'keo-pi', en: 'coffee', picture: '☕' },
    { ko: '차', roman: 'cha', en: 'tea', picture: '🍵', note: '차 also means “car”.' },
    { ko: '술', roman: 'sul', en: 'alcohol', picture: '🍺' },
    { ko: '매워요', roman: 'mae-wo-yo', en: "it's spicy", picture: '🌶️' },
    { ko: '짜요', roman: 'jja-yo', en: "it's salty", picture: '🧂' },
  ]),
  vocabLesson('home', 'Home & dorm', 'Room, bed, toilet, kitchen and fridge', [
    { ko: '방', roman: 'bang', en: 'room' },
    { ko: '침대', roman: 'chim-dae', en: 'bed', picture: '🛏️' },
    { ko: '화장실', roman: 'hwa-jang-sil', en: 'toilet, bathroom', picture: '🚽' },
    { ko: '부엌', roman: 'bu-eok', en: 'kitchen' },
    { ko: '냉장고', roman: 'naeng-jang-go', en: 'fridge' },
    { ko: '세탁기', roman: 'se-tak-gi', en: 'washing machine' },
    { ko: '에어컨', roman: 'e-eo-keon', en: 'air conditioner' },
    { ko: '문', roman: 'mun', en: 'door', picture: '🚪' },
    { ko: '창문', roman: 'chang-mun', en: 'window', picture: '🪟' },
    { ko: '열쇠', roman: 'yeol-soe', en: 'key', picture: '🔑' },
  ]),
  vocabLesson('weather', 'Weather', 'Rain, snow, wind, hot and cold', [
    { ko: '날씨', roman: 'nal-ssi', en: 'weather' },
    { ko: '비', roman: 'bi', en: 'rain', picture: '🌧️' },
    { ko: '눈이 와요', roman: 'nu-ni wa-yo', en: "it's snowing", picture: '❄️' },
    { ko: '바람', roman: 'ba-ram', en: 'wind', picture: '🌬️' },
    { ko: '더워요', roman: 'deo-wo-yo', en: "it's hot (weather)", picture: '☀️' },
    { ko: '추워요', roman: 'chu-wo-yo', en: "it's cold (weather)", picture: '🥶' },
    { ko: '맑아요', roman: 'mal-ga-yo', en: "it's clear" },
    { ko: '흐려요', roman: 'heu-ryeo-yo', en: "it's cloudy", picture: '☁️' },
    { ko: '우산', roman: 'u-san', en: 'umbrella', picture: '☂️' },
    { ko: '장마', roman: 'jang-ma', en: 'rainy season' },
  ]),
  vocabLesson('clothes', 'Clothes', 'Clothes, trousers, shoes, socks and a mask', [
    { ko: '옷', roman: 'ot', en: 'clothes', picture: '👕' },
    { ko: '바지', roman: 'ba-ji', en: 'trousers', picture: '👖' },
    { ko: '신발', roman: 'sin-bal', en: 'shoes', picture: '👟' },
    { ko: '셔츠', roman: 'syeo-cheu', en: 'shirt', picture: '👔' },
    { ko: '양말', roman: 'yang-mal', en: 'socks', picture: '🧦' },
    { ko: '작업복', roman: 'ja-geop-bok', en: 'work clothes' },
    { ko: '안전화', roman: 'an-jeon-hwa', en: 'safety shoes' },
    { ko: '마스크', roman: 'ma-seu-keu', en: 'face mask', picture: '😷' },
    { ko: '치마', roman: 'chi-ma', en: 'skirt' },
    { ko: '외투', roman: 'oe-tu', en: 'coat', picture: '🧥' },
  ]),
  vocabLesson('phone', 'Phone & internet', 'Mobile, text, number, wifi and charger', [
    { ko: '휴대폰', roman: 'hyu-dae-pon', en: 'mobile phone', picture: '📱' },
    { ko: '전화', roman: 'jeon-hwa', en: 'phone call', picture: '📞' },
    { ko: '문자', roman: 'mun-ja', en: 'text message', picture: '💬' },
    { ko: '전화번호', roman: 'jeon-hwa-beon-ho', en: 'phone number' },
    { ko: '와이파이', roman: 'wa-i-pa-i', en: 'wifi', picture: '📶' },
    { ko: '충전기', roman: 'chung-jeon-gi', en: 'charger', picture: '🔋' },
    { ko: '인터넷', roman: 'in-teo-net', en: 'internet' },
    { ko: '사진', roman: 'sa-jin', en: 'photo', picture: '📷' },
    { ko: '영상 통화', roman: 'yeong-sang tong-hwa', en: 'video call' },
    { ko: '비밀번호', roman: 'bi-mil-beon-ho', en: 'password', picture: '🔒' },
  ]),
  vocabLesson('feelings', 'Feelings', 'Glad, sad, tired, worried and homesick', [
    { ko: '기뻐요', roman: 'gi-ppeo-yo', en: "I'm glad", picture: '😊' },
    { ko: '슬퍼요', roman: 'seul-peo-yo', en: "I'm sad", picture: '😢' },
    { ko: '피곤해요', roman: 'pi-gon-hae-yo', en: "I'm tired", picture: '😩' },
    { ko: '화나요', roman: 'hwa-na-yo', en: "I'm angry", picture: '😠' },
    { ko: '걱정돼요', roman: 'geok-jeong-dwae-yo', en: "I'm worried", picture: '😟' },
    { ko: '심심해요', roman: 'sim-sim-hae-yo', en: "I'm bored" },
    { ko: '무서워요', roman: 'mu-seo-wo-yo', en: "I'm scared", picture: '😨' },
    { ko: '보고 싶어요', roman: 'bo-go si-peo-yo', en: 'I miss you' },
    { ko: '행복해요', roman: 'haeng-bo-kae-yo', en: "I'm happy", picture: '😄' },
    { ko: '외로워요', roman: 'oe-ro-wo-yo', en: "I'm lonely" },
  ]),
  vocabLesson('symptoms', 'Symptoms', 'Fever, cough, a cold, a headache and a wound', [
    { ko: '열', roman: 'yeol', en: 'fever', picture: '🤒' },
    { ko: '기침', roman: 'gi-chim', en: 'cough' },
    { ko: '감기', roman: 'gam-gi', en: 'a cold', picture: '🤧' },
    { ko: '두통', roman: 'du-tong', en: 'headache' },
    { ko: '배탈', roman: 'bae-tal', en: 'upset stomach' },
    { ko: '상처', roman: 'sang-cheo', en: 'wound', picture: '🩹' },
    { ko: '주사', roman: 'ju-sa', en: 'injection', picture: '💉' },
    { ko: '입원', roman: 'i-bwon', en: 'hospital stay' },
    { ko: '진통제', roman: 'jin-tong-je', en: 'painkiller', picture: '💊' },
    { ko: '소화제', roman: 'so-hwa-je', en: 'digestive medicine' },
  ]),
  vocabLesson('more-shopping', 'Shopping', 'Discount, receipt, refund, cash and a bag', [
    { ko: '할인', roman: 'ha-rin', en: 'discount', picture: '🏷️' },
    { ko: '영수증', roman: 'yeong-su-jeung', en: 'receipt', picture: '🧾' },
    { ko: '교환', roman: 'gyo-hwan', en: 'exchange' },
    { ko: '환불', roman: 'hwan-bul', en: 'refund' },
    { ko: '크기', roman: 'keu-gi', en: 'size' },
    { ko: '시장', roman: 'si-jang', en: 'market' },
    { ko: '편의점', roman: 'pyeo-ni-jeom', en: 'convenience store', picture: '🏪' },
    { ko: '봉투', roman: 'bong-tu', en: 'bag', picture: '🛍️' },
    { ko: '계산해요', roman: 'gye-san-hae-yo', en: 'pay (at the till)' },
    { ko: '현금', roman: 'hyeon-geum', en: 'cash', picture: '💵' },
  ]),
  vocabLesson('around-town', 'Around town', 'Post office, police station, immigration and job centre', [
    { ko: '우체국', roman: 'u-che-guk', en: 'post office', picture: '📮' },
    { ko: '경찰서', roman: 'gyeong-chal-seo', en: 'police station' },
    { ko: '출입국 사무소', roman: 'chu-rip-guk sa-mu-so', en: 'immigration office' },
    { ko: '고용센터', roman: 'go-yong-sen-teo', en: 'job centre', note: 'The government job centre handles EPS matters such as changing workplace.' },
    { ko: '대사관', roman: 'dae-sa-gwan', en: 'embassy' },
    { ko: '공원', roman: 'gong-won', en: 'park', picture: '🌳' },
    { ko: '교회', roman: 'gyo-hoe', en: 'church', picture: '⛪' },
    { ko: '절', roman: 'jeol', en: 'temple', picture: '🛕' },
    { ko: '마트', roman: 'ma-teu', en: 'supermarket', picture: '🛒' },
    { ko: '미용실', roman: 'mi-yong-sil', en: 'hair salon', picture: '💇' },
  ]),
];
